import { MAX_TEXT_LENGTH } from "../../../utils/questionUtils";
import { WorkflowSpec, WorkflowItem } from "./types";

/** Max serialized payload size and nesting depth (mirrors the Android guards). */
export const MAX_WORKFLOW_ANSWERS_BYTES = 100 * 1024;
export const MAX_WORKFLOW_ANSWERS_DEPTH = 10;

/**
 * Build one item object from a spec's ordered fields. Every field is emitted
 * (in `spec.fields` order) coerced to a string, with missing values as `""` —
 * the single choke-point that keeps the wire payload all-strings and keeps
 * columnar arrays index-aligned. Never drops keys.
 */
const buildItemObject = (
  spec: WorkflowSpec,
  item: WorkflowItem,
  omitBlank = false,
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const { fieldKey } of spec.fields) {
    const value = item[fieldKey];
    const str = value == null ? "" : String(value);
    // Object-list items omit blank fields (the cross-client contract: each item
    // carries only the fields the participant filled). The columnar/flat callers
    // keep "" so their parallel arrays / fixed shape stay aligned.
    if (omitBlank && str === "") {
      continue;
    }
    out[fieldKey] = str;
  }
  return out;
};

/**
 * Build the `workflowAnswers` object for a workflow. The shape is a hard
 * cross-client/backend contract (must be byte-identical to iOS/Android):
 *
 * - no fields (confirm-only, e.g. `appointment`)        → `{}`
 * - non-repeatable, no entry / negative root            → `{}`
 * - non-repeatable                                      → flat `{ fieldKey: value }`
 * - repeatable, `serializeAs: "columnar"` (default)     → columnar parallel
 *   arrays `{ fieldKey: [value, ...] }`, one array per field, index-aligned
 * - repeatable, `serializeAs: "objectList"`             → an array of per-item
 *   objects wrapped under the workflow's own name:
 *   `{ [workflowName]: [{ fieldA, fieldB, ... }, ...] }` (`vehicles`, `employment`)
 *
 * Matches Android `V3AnswerAssembler.buildWorkflowAnswers`: for the columnar
 * contract the workflow name is never a key inside `workflowAnswers`; the
 * `objectList` contract is the exception (the officer-side schema for
 * `vehicles`/`employment` expects nested per-item objects). Field order follows
 * `spec.fields`. Columnar/flat shapes keep missing values as `""` (so parallel
 * arrays / the fixed object stay aligned); object-list items instead omit blank
 * fields, so each item carries only what the participant filled. Purely a
 * function of (workflowName, spec, items) — no root-answer branch (the caller
 * passes zero items for a negative/empty answer).
 */
export const buildWorkflowAnswers = (
  workflowName: string,
  spec: WorkflowSpec,
  items: WorkflowItem[],
): Record<string, unknown> => {
  if (spec.fields.length === 0) {
    return {};
  }

  if (!spec.repeatable) {
    const item = items[0];
    if (!item) {
      return {};
    }
    return buildItemObject(spec, item);
  }

  if (spec.serializeAs === "objectList") {
    // Repeatable, multi-field, "array-of-objects wrapped under the workflow's
    // own key" contract (`vehicles`, `employment`) — distinct from the columnar
    // contract below because the officer-side schema expects nested per-item
    // objects, not parallel arrays, for these workflows. Blank fields are omitted
    // per the contract (each item carries only the fields the participant filled).
    return {
      [workflowName]: items.map((item) => buildItemObject(spec, item, true)),
    };
  }

  // Repeatable (default) → columnar parallel arrays: one array per fieldKey,
  // index-aligned across items. Single- and multi-field repeatables share this
  // exact shape.
  const columns: Record<string, string[]> = {};
  for (const { fieldKey } of spec.fields) {
    columns[fieldKey] = items.map((item) => {
      const value = item[fieldKey];
      return value == null ? "" : String(value);
    });
  }
  return columns;
};

/** Serialize the workflow answers to the AWSJSON string sent to the backend. */
export const serializeWorkflowAnswers = (
  workflowName: string,
  spec: WorkflowSpec,
  items: WorkflowItem[],
): string => JSON.stringify(buildWorkflowAnswers(workflowName, spec, items));

/**
 * UTF-8 byte length of a string, computed without `TextEncoder` (which is not
 * present in the jsdom test environment). A valid surrogate pair counts as 4
 * bytes; an unpaired surrogate counts as 3 (it is not a valid code point and
 * is not allowed to consume the following unit).
 */
export const utf8ByteLength = (str: string): number => {
  let bytes = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (
      code >= 0xd800 &&
      code <= 0xdbff &&
      i + 1 < str.length &&
      str.charCodeAt(i + 1) >= 0xdc00 &&
      str.charCodeAt(i + 1) <= 0xdfff
    ) {
      // Valid high+low surrogate pair — one 4-byte code point.
      bytes += 4;
      i++;
    } else {
      // BMP code point or an unpaired surrogate.
      bytes += 3;
    }
  }
  return bytes;
};

/** Iterative nesting depth: scalars = 0, each object/array = 1 + max child. */
const jsonDepth = (root: unknown): number => {
  let max = 0;
  const stack: Array<{ value: unknown; depth: number }> = [
    { value: root, depth: 0 },
  ];
  while (stack.length > 0) {
    const { value, depth } = stack.pop()!;
    if (Array.isArray(value)) {
      const d = depth + 1;
      if (d > max) max = d;
      for (const child of value) stack.push({ value: child, depth: d });
    } else if (value !== null && typeof value === "object") {
      const d = depth + 1;
      if (d > max) max = d;
      for (const child of Object.values(value as Record<string, unknown>)) {
        stack.push({ value: child, depth: d });
      }
    }
  }
  return max;
};

/** Any string leaf longer than the per-field cap. */
const hasOverlongField = (root: unknown): boolean => {
  const stack: unknown[] = [root];
  while (stack.length > 0) {
    const value = stack.pop();
    if (typeof value === "string") {
      if (value.length > MAX_TEXT_LENGTH) return true;
    } else if (Array.isArray(value)) {
      stack.push(...value);
    } else if (value !== null && typeof value === "object") {
      stack.push(...Object.values(value as Record<string, unknown>));
    }
  }
  return false;
};

export type WorkflowAnswersGuard =
  | { ok: true }
  | { ok: false; reason: "TOO_LARGE" | "TOO_DEEP" | "FIELD_TOO_LONG" };

/**
 * Validate a built `workflowAnswers` payload before submission. Checks size
 * first (cheap, on raw UTF-8 bytes) so an oversized blob is rejected before any
 * traversal, then per-field length, then nesting depth. Any failure rejects the
 * whole form (defense-in-depth; the backend re-checks).
 */
export const guardWorkflowAnswers = (
  answers: Record<string, unknown>,
): WorkflowAnswersGuard => {
  const json = JSON.stringify(answers);
  if (utf8ByteLength(json) > MAX_WORKFLOW_ANSWERS_BYTES) {
    return { ok: false, reason: "TOO_LARGE" };
  }
  if (hasOverlongField(answers)) {
    return { ok: false, reason: "FIELD_TOO_LONG" };
  }
  if (jsonDepth(answers) > MAX_WORKFLOW_ANSWERS_DEPTH) {
    return { ok: false, reason: "TOO_DEEP" };
  }
  return { ok: true };
};

/**
 * Safely parse a `questionPrepopulation` AWSJSON string into a WorkflowItem.
 * Only keys present in `spec.fields` are copied (a strict allowlist — never the
 * source's own keys), and the result is a null-prototype object. Because the
 * allowlist is the trusted registry field set (which never contains
 * `__proto__`/`constructor`/`prototype`), a malicious prepopulation blob can
 * neither pollute `Object.prototype` nor inject fields the workflow doesn't
 * define. Returns an empty item on missing/invalid JSON.
 */
export const parseWorkflowPrepopulation = (
  valuesJson: string | null | undefined,
  spec: WorkflowSpec,
): WorkflowItem => {
  const item: WorkflowItem = Object.create(null);
  if (!valuesJson) {
    return item;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(valuesJson);
  } catch {
    return item;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return item;
  }
  const source = parsed as Record<string, unknown>;
  for (const { fieldKey } of spec.fields) {
    if (Object.prototype.hasOwnProperty.call(source, fieldKey)) {
      const value = source[fieldKey];
      if (value != null && typeof value !== "object") {
        item[fieldKey] = String(value);
      }
    }
  }
  return item;
};

/**
 * Inverse of the repeatable encoding (either `serializeAs` shape): parse a
 * `workflowAnswers` AWSJSON string back into an ordered list of WorkflowItems
 * for back-nav re-display. Only `spec.fields` keys are read (a strict allowlist
 * into null-prototype items — same prototype-pollution safety as
 * `parseWorkflowPrepopulation`). Returns `[]` on missing/invalid JSON or when
 * there's nothing to seed from.
 *
 * - `serializeAs: "objectList"` (`workflowName` required to find the wrapper
 *   key) — reads the array at `source[workflowName]`; each entry's `spec.fields`
 *   keys are copied (missing/non-scalar → `""`); a non-array/absent wrapper → `[]`.
 * - `serializeAs: "columnar"` (default) — parallel arrays `{ fieldKey: [...] }`,
 *   ragged columns padded to the longest one so every item carries every field
 *   (missing → `""`).
 */
export const parseRepeatableItems = (
  valuesJson: string | null | undefined,
  spec: WorkflowSpec,
  workflowName?: string,
): WorkflowItem[] => {
  if (!valuesJson) {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(valuesJson);
  } catch {
    return [];
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return [];
  }
  const source = parsed as Record<string, unknown>;

  if (spec.serializeAs === "objectList") {
    const raw =
      workflowName && Object.prototype.hasOwnProperty.call(source, workflowName)
        ? source[workflowName]
        : undefined;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((entry) => {
      const entrySource =
        entry !== null && typeof entry === "object" && !Array.isArray(entry)
          ? (entry as Record<string, unknown>)
          : {};
      const item: WorkflowItem = Object.create(null);
      for (const { fieldKey } of spec.fields) {
        const v = Object.prototype.hasOwnProperty.call(entrySource, fieldKey)
          ? entrySource[fieldKey]
          : undefined;
        item[fieldKey] = v == null || typeof v === "object" ? "" : String(v);
      }
      return item;
    });
  }

  // Read each spec field's column (arrays only; anything else is an empty column).
  const columns: Record<string, string[]> = Object.create(null);
  let length = 0;
  for (const { fieldKey } of spec.fields) {
    const raw = Object.prototype.hasOwnProperty.call(source, fieldKey)
      ? source[fieldKey]
      : undefined;
    const col = Array.isArray(raw)
      ? raw.map((v) => (v == null || typeof v === "object" ? "" : String(v)))
      : [];
    columns[fieldKey] = col;
    if (col.length > length) {
      length = col.length;
    }
  }
  const items: WorkflowItem[] = [];
  for (let i = 0; i < length; i++) {
    const item: WorkflowItem = Object.create(null);
    for (const { fieldKey } of spec.fields) {
      item[fieldKey] = columns[fieldKey][i] ?? "";
    }
    items.push(item);
  }
  return items;
};
